import { cn } from "@/lib/utils";

export {
  ENUM_MENU_MAX_ITEMS,
  ENUM_ACTION_SHEET_MAX_ITEMS,
  SEARCHABLE_THRESHOLD,
  VIRTUALIZE_THRESHOLD,
  shouldUseClassicPicker,
  shouldUseSearchablePicker,
} from "@/lib/dropdown-constants";

/**
 * Two public dropdown families — do not add parallel picker/menu components:
 * 1. SearchableDropdown — lists with search (taxonomy, countries, currencies, …)
 * 2. Classic dropdown — Radix DropdownMenu (actions/menus/checkboxes) or Select (simple pickers, no search)
 *
 * Panel chrome (three surfaces, same item shape):
 * - `panel` — popover ladder, neutral-800 / neutral-600 in dark (SearchableDropdown, compose To:, page pickers)
 * - `chrome` — the `--sheet*` surface, i.e. matches the overlay panel the menu was opened from: neutral-900 / neutral-700 in dark, light panel on a light site (Radix menus inside sheets/dialog cards)
 * - `glass` — blur bg-black/70 border-white/12 (feed filter panels, sidebar nav flyouts, chat sidebar overflow)
 *
 * Legacy aliases: `nav` → panel, `dialog` → chrome (same tokens).
 * Items use rounded-full pill highlights on all surfaces.
 */

export type DropdownSurface = "panel" | "chrome" | "glass" | "nav" | "dialog";

export type DropdownPanelSurface = "panel" | "chrome";

export function normalizeDropdownSurface(surface: DropdownSurface = "panel"): DropdownPanelSurface | "glass" {
  if (surface === "glass") return "glass";
  if (surface === "chrome" || surface === "dialog") return "chrome";
  return "panel";
}

export const DROPDOWN_PANEL_SURFACE_CLASS =
  "bg-popover border-border text-popover-foreground dark:bg-neutral-800 dark:border-neutral-600 dark:text-white";

/**
 * THEME-FOLLOWING since Phase 2 step 2f (2f-acceptance find): `chrome` means
 * "the same surface as the sheet/dialog the menu was opened from", and since
 * the overlay panels follow the site theme, so does this. It paints with the
 * same `--sheet*` tokens as `SHEET_PANEL_DARK` — in dark these resolve to the
 * former literals byte for byte (`#171717` = `bg-neutral-900`, `#404040` =
 * `bg-neutral-700`; foreground `#fafafa` vs `#ffffff` is the one approved ≈).
 *
 * The forced `dark` it used to carry existed because a chrome menu portals to
 * `document.body` and escapes the panel's scope — which was forced-dark
 * pre-2f. Now the page theme IS the panel theme, so the portal resolves the
 * right set on its own; a re-forced `dark` here would repaint the old bug in
 * mirror image (dark menu dropping out of a light sheet).
 */
export const DROPDOWN_CHROME_SURFACE_CLASS =
  "bg-sheet border-sheet-handle text-sheet-foreground";

/** @deprecated Use DROPDOWN_PANEL_SURFACE_CLASS */
export const DROPDOWN_NAV_PANEL_CLASS = DROPDOWN_PANEL_SURFACE_CLASS;

/** @deprecated Use DROPDOWN_CHROME_SURFACE_CLASS */
export const DROPDOWN_DIALOG_PANEL_CLASS = DROPDOWN_CHROME_SURFACE_CLASS;

export const DROPDOWN_GLASS_PANEL_CLASS =
  "backdrop-blur-2xl border-white/12 bg-black/70 text-white shadow-[0_-18px_60px_rgba(0,0,0,0.55),0_2px_10px_rgba(0,0,0,0.25)]";

export const DROPDOWN_ITEM_PRESS_CLASS =
  "hover:bg-accent hover:text-accent-foreground active:bg-accent focus:bg-accent focus:text-accent-foreground data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-disabled:opacity-50 dark:hover:bg-neutral-700 dark:active:bg-neutral-600 dark:focus:bg-neutral-700 dark:focus:text-white dark:data-[highlighted]:bg-neutral-700 dark:data-[highlighted]:text-white";

export const DROPDOWN_PANEL_ITEM_CLASS = cn(
  "text-popover-foreground dark:text-white",
  DROPDOWN_ITEM_PRESS_CLASS,
);

export const DROPDOWN_NAV_ITEM_CLASS = DROPDOWN_PANEL_ITEM_CLASS;

export const DROPDOWN_GLASS_ITEM_CLASS = cn(
  "text-white",
  DROPDOWN_ITEM_PRESS_CLASS,
  "dark:hover:bg-neutral-700 dark:active:bg-neutral-600 dark:focus:bg-neutral-700 dark:focus:text-white dark:text-white",
);

export const DROPDOWN_SEPARATOR_CLASS = "bg-border dark:bg-neutral-600/60";

export const DROPDOWN_CHROME_SEPARATOR_CLASS = "bg-sheet-handle/60";

export const DROPDOWN_PANEL_CONTENT_BASE =
  "z-1500 min-w-32 overflow-hidden rounded-3xl border p-2 shadow-2xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2";

export const DROPDOWN_MENU_CONTENT_BASE = DROPDOWN_PANEL_CONTENT_BASE;

export const DROPDOWN_ITEM_BASE =
  "relative flex cursor-default select-none items-center whitespace-nowrap outline-hidden transition-colors data-disabled:pointer-events-none";

export const DROPDOWN_ITEM_SHAPE_CLASS = "rounded-full px-4 py-2.5 text-base font-medium";

export const DROPDOWN_ITEM_CLASS = cn(DROPDOWN_ITEM_BASE, DROPDOWN_ITEM_SHAPE_CLASS, DROPDOWN_PANEL_ITEM_CLASS);

export const DROPDOWN_MENU_ITEM_BASE = DROPDOWN_ITEM_BASE;

export const DROPDOWN_MENU_ITEM_CLASS = DROPDOWN_ITEM_CLASS;

export const DROPDOWN_MENU_CHECKBOX_ITEM_CLASS = cn(
  DROPDOWN_ITEM_BASE,
  "justify-between rounded-full px-4 py-2.5 text-base",
  DROPDOWN_PANEL_ITEM_CLASS,
);

export const DROPDOWN_MENU_RADIO_ITEM_CLASS = cn(
  DROPDOWN_ITEM_BASE,
  "rounded-full py-2.5 pl-8 pr-4 text-base",
  DROPDOWN_PANEL_ITEM_CLASS,
);

export const DROPDOWN_MENU_SUB_CONTENT_BASE =
  "z-1510 min-w-32 overflow-hidden rounded-3xl border p-2 shadow-2xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2";

export const DROPDOWN_MENU_SUB_TRIGGER_CLASS = cn(
  DROPDOWN_ITEM_BASE,
  "rounded-full px-4 py-2.5 text-base",
  DROPDOWN_PANEL_ITEM_CLASS,
  "data-[state=open]:bg-accent dark:data-[state=open]:bg-neutral-700",
);

/** Matches SHEET_Z_PICKER (10140) — above dialog cards and sheets. */
export const DROPDOWN_CHROME_PORTAL_Z_CLASS = "z-[10140]";

export const SELECT_CONTENT_BASE =
  "max-h-96 min-w-32 overflow-hidden rounded-3xl border shadow-2xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2";

export function selectPortalZClass(surface: DropdownSurface = "panel"): string {
  return normalizeDropdownSurface(surface) === "chrome"
    ? DROPDOWN_CHROME_PORTAL_Z_CLASS
    : "z-1200";
}

export function dropdownMenuPortalZClass(surface: DropdownSurface = "panel"): string {
  return normalizeDropdownSurface(surface) === "chrome"
    ? DROPDOWN_CHROME_PORTAL_Z_CLASS
    : "z-1500";
}

export const SELECT_ITEM_CLASS = cn(
  DROPDOWN_ITEM_BASE,
  "w-full rounded-full py-2.5 pl-8 pr-4 text-base data-disabled:opacity-50",
  DROPDOWN_PANEL_ITEM_CLASS,
);

export const FIELD_SURFACE_RADIUS = "rounded-3xl";

export const FIELD_SURFACE_HEIGHT = "h-12";

export const FIELD_SURFACE_SEARCH_HEIGHT = "h-10";

export const FIELD_SURFACE_PANEL =
  "border border-border bg-background text-foreground dark:border-neutral-600 dark:bg-neutral-800 dark:text-white";

export const FIELD_SURFACE_PANEL_HOVER = "hover:bg-accent dark:hover:bg-neutral-700";

/** @deprecated Use FIELD_SURFACE_PANEL */
export const FIELD_SURFACE_NAV = FIELD_SURFACE_PANEL;

/** @deprecated Use FIELD_SURFACE_PANEL_HOVER */
export const FIELD_SURFACE_NAV_HOVER = FIELD_SURFACE_PANEL_HOVER;

/* Chrome fields sit IN the sheet (not portalled), so they were equally
   forced-dark pre-2f; same tokenisation as the chrome menu panel above.
   `hover:bg-accent` in dark = `0 0% 14.9%` ≈ `#262626` = the former
   `hover:bg-neutral-800`, byte for byte. */
export const FIELD_SURFACE_CHROME =
  "border border-sheet-handle bg-sheet text-sheet-foreground";

export const FIELD_SURFACE_CHROME_HOVER = "hover:bg-accent";

export const FIELD_SURFACE_PANEL_CLASS = cn(FIELD_SURFACE_PANEL, FIELD_SURFACE_RADIUS);

/** @deprecated Use FIELD_SURFACE_PANEL_CLASS */
export const FIELD_SURFACE_NAV_CLASS = FIELD_SURFACE_PANEL_CLASS;

export const FIELD_SURFACE_FOCUS_RING_CLASS =
  "focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:relative focus:z-10 dark:focus:ring-neutral-500";

export const FIELD_SURFACE_FOCUS_VISIBLE_CLASS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:focus-visible:ring-neutral-500 dark:focus-visible:ring-offset-neutral-900";

export const FIELD_SURFACE_INPUT_CLASS = cn(
  FIELD_SURFACE_PANEL_CLASS,
  FIELD_SURFACE_HEIGHT,
  "px-4 text-base placeholder:text-muted-foreground dark:placeholder:text-neutral-400",
  FIELD_SURFACE_FOCUS_VISIBLE_CLASS,
);

export const FIELD_SURFACE_SEARCH_SHELL_CLASS = cn(
  FIELD_SURFACE_PANEL_CLASS,
  FIELD_SURFACE_SEARCH_HEIGHT,
);

export const SEARCHABLE_DROPDOWN_TRIGGER_BASE = cn(
  "flex w-full cursor-pointer items-center justify-between border px-4 text-base transition-colors disabled:cursor-not-allowed disabled:opacity-50",
  FIELD_SURFACE_RADIUS,
  FIELD_SURFACE_HEIGHT,
  FIELD_SURFACE_FOCUS_RING_CLASS,
);

export const SEARCHABLE_DROPDOWN_TRIGGER_CLASS = cn(
  FIELD_SURFACE_PANEL,
  FIELD_SURFACE_PANEL_HOVER,
  "focus:ring-ring dark:focus:ring-neutral-500",
);

/** @deprecated Use SEARCHABLE_DROPDOWN_TRIGGER_CLASS */
export const SEARCHABLE_DROPDOWN_NAV_TRIGGER_CLASS = SEARCHABLE_DROPDOWN_TRIGGER_CLASS;

export const SEARCHABLE_DROPDOWN_DIALOG_TRIGGER_CLASS =
  "border-sheet-handle bg-sheet text-sheet-foreground hover:bg-accent focus:ring-ring dark:focus:ring-neutral-500 focus:ring-offset-sheet";

export const SEARCHABLE_DROPDOWN_PANEL_SURFACE_CLASS =
  "max-h-60 rounded-3xl bg-popover border border-border dark:bg-neutral-800 dark:border-neutral-600";

/** @deprecated Use SEARCHABLE_DROPDOWN_PANEL_SURFACE_CLASS */
export const SEARCHABLE_DROPDOWN_NAV_PANEL_CLASS = SEARCHABLE_DROPDOWN_PANEL_SURFACE_CLASS;

export const SEARCHABLE_DROPDOWN_DIALOG_PANEL_CLASS =
  "max-h-60 rounded-3xl bg-sheet border border-sheet-handle";

export const SEARCHABLE_DROPDOWN_SEARCH_SHELL_CLASS = FIELD_SURFACE_SEARCH_SHELL_CLASS;

export const SEARCHABLE_DROPDOWN_SEARCH_INPUT_CLASS = cn(
  "h-full w-full border-0 bg-transparent px-4 text-base text-foreground placeholder:text-muted-foreground focus:outline-hidden focus:ring-2 focus:ring-ring dark:text-white dark:placeholder:text-neutral-400 dark:focus:ring-neutral-500",
);

export const SEARCHABLE_DROPDOWN_OPTION_BASE =
  "group relative flex touch-manipulation items-center gap-2 cursor-pointer select-none rounded-full px-4 py-2 text-base transition-colors [-webkit-tap-highlight-color:transparent]";

export const SEARCHABLE_DROPDOWN_OPTION_CLASS = cn(
  SEARCHABLE_DROPDOWN_OPTION_BASE,
  "text-popover-foreground hover:bg-accent active:bg-accent data-[pressed=true]:bg-accent dark:text-white dark:hover:bg-neutral-700 dark:active:bg-neutral-600 dark:data-[pressed=true]:bg-neutral-600",
);

export const SEARCHABLE_DROPDOWN_MUTED_TEXT_CLASS = "text-muted-foreground dark:text-neutral-400";

export function dropdownPanelClass(surface: DropdownSurface = "panel", className?: string) {
  const normalized = normalizeDropdownSurface(surface);
  const panel =
    normalized === "glass"
      ? DROPDOWN_GLASS_PANEL_CLASS
      : normalized === "chrome"
        ? DROPDOWN_CHROME_SURFACE_CLASS
        : DROPDOWN_PANEL_SURFACE_CLASS;
  return cn(panel, className);
}

export function dropdownShellClass(surface: DropdownSurface = "panel", className?: string) {
  return cn(DROPDOWN_PANEL_CONTENT_BASE, dropdownPanelClass(surface), className);
}

export function dropdownItemClass(surface: DropdownSurface = "panel", className?: string) {
  const item = normalizeDropdownSurface(surface) === "glass" ? DROPDOWN_GLASS_ITEM_CLASS : DROPDOWN_PANEL_ITEM_CLASS;
  return cn(item, className);
}

export function dropdownMenuItemClass(surface: DropdownSurface = "panel", className?: string) {
  return cn(DROPDOWN_ITEM_BASE, DROPDOWN_ITEM_SHAPE_CLASS, dropdownItemClass(surface), className);
}

export function dropdownLinkItemClass(surface: DropdownSurface = "panel", className?: string) {
  return cn(dropdownMenuItemClass(surface), "gap-3 no-underline", className);
}

export function dropdownMenuCheckboxItemClass(surface: DropdownSurface = "panel", className?: string) {
  return cn(
    DROPDOWN_ITEM_BASE,
    "justify-between rounded-full px-4 py-2.5 text-base",
    dropdownItemClass(surface),
    className,
  );
}

export function dropdownMenuRadioItemClass(surface: DropdownSurface = "panel", className?: string) {
  return cn(
    DROPDOWN_ITEM_BASE,
    "rounded-full py-2.5 pl-8 pr-4 text-base",
    dropdownItemClass(surface),
    className,
  );
}

export function dropdownMenuSubTriggerClass(surface: DropdownSurface = "panel", className?: string) {
  return cn(
    DROPDOWN_ITEM_BASE,
    "rounded-full px-4 py-2.5 text-base",
    dropdownItemClass(surface),
    "data-[state=open]:bg-accent dark:data-[state=open]:bg-neutral-700",
    className,
  );
}

export function searchableDropdownTriggerClass() {
  return SEARCHABLE_DROPDOWN_TRIGGER_CLASS;
}

export function searchableDropdownPanelClass() {
  return SEARCHABLE_DROPDOWN_PANEL_SURFACE_CLASS;
}

export function searchableDropdownComposeSuggestionsClass(className?: string) {
  return cn(searchableDropdownPanelClass(), "w-full max-w-sm", className);
}

export const GLASS_DROPDOWN_CONTENT_CLASS = DROPDOWN_GLASS_PANEL_CLASS;

export const GLASS_DROPDOWN_ITEM_CLASS = DROPDOWN_GLASS_ITEM_CLASS;

export const SELECT_PANEL_TRIGGER_CLASS = cn(
  FIELD_SURFACE_PANEL_CLASS,
  FIELD_SURFACE_PANEL_HOVER,
  "rounded-2xl focus:ring-ring dark:focus:ring-neutral-500",
);

/** @deprecated Use SELECT_PANEL_TRIGGER_CLASS */
export const SELECT_NAV_TRIGGER_CLASS = SELECT_PANEL_TRIGGER_CLASS;

export const SELECT_CHROME_TRIGGER_CLASS = cn(
  FIELD_SURFACE_RADIUS,
  FIELD_SURFACE_CHROME,
  FIELD_SURFACE_CHROME_HOVER,
  "focus:ring-ring dark:focus:ring-neutral-500 focus:ring-offset-sheet",
);

export const SELECT_DIALOG_TRIGGER_CLASS =
  "rounded-3xl border-sheet-handle bg-sheet text-sheet-foreground hover:bg-accent focus:ring-ring dark:focus:ring-neutral-500 focus:ring-offset-sheet";

export function selectTriggerClass(surface: DropdownSurface = "panel", className?: string) {
  const trigger =
    normalizeDropdownSurface(surface) === "chrome" ? SELECT_CHROME_TRIGGER_CLASS : SELECT_PANEL_TRIGGER_CLASS;
  return cn(trigger, className);
}
